import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function Dashboard() {
  const [records, setRecords] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const prior = new Date(today);
    prior.setDate(today.getDate() - 40);
    return prior.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [lastUploadedDate, setLastUploadedDate] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const tableRef = useRef(null);
  const isFirstLoad = useRef(true); // skip toast on first load

  const [traineeFilter, setTraineeFilter] = useState("");

  const fetchData = useCallback(async () => {
  try {
    const response = await axios.get(
      "https://dashboard.cohere.ph/api/fetch_timerecords.php",
      {
        params: {
          start_date: startDate,
          end_date: endDate,
          search: searchTerm.trim(),
          trainee_filter: traineeFilter,
        },
      }
    );

    // Keep mapping ONLY if UI uses companyid
    const transformed = response.data.map((rec) => ({
      companyid: rec.EmployeeID,
      FirstName: rec.FirstName,
      LastName: rec.LastName,
      Day: rec.Day,
      Time: rec.Time,
      Type: rec.Type,
    }));

    setRecords(transformed);

    // avoid toast spam on auto-fetch
    if (!isFirstLoad.current) {
      toast.success("Records loaded successfully", {
        toastId: "records-loaded",
        autoClose: 2000,
      });
    }

    isFirstLoad.current = false;

  } catch (error) {
    console.error("Error fetching records", error);
    toast.error("Failed to fetch records", { autoClose: 4000 });
  }
}, [startDate, endDate, searchTerm, traineeFilter]);

  const fetchLastUpload = async () => {
    try {
      const response = await axios.get("https://dashboard.cohere.ph/api/last_upload.php");
      setLastUploadedDate(response.data.last_uploaded_date);
    } catch (error) {
      console.error("Error fetching last uploaded date", error);
      toast.error("Failed to fetch last uploaded date", { autoClose: 4000 });
    }
  };

  useEffect(() => {
  fetchData();
}, [startDate, endDate, searchTerm, traineeFilter]);

  useEffect(() => {
    fetchLastUpload();
  }, []);

  useEffect(() => {
    document.title = "Timerecords";
  }, []);

  const handleCopyTable = () => {
    const table = tableRef.current;
    if (!table) return toast.error("Table not found!");

    const rows = table.querySelectorAll("tr");
    const text = Array.from(rows).map(row => {
      return Array.from(row.querySelectorAll("th, td"))
        .map(col => col.innerText.trim())
        .join("\t");
    }).join("\n");

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text)
        .then(() => toast.success("Table copied to clipboard!", { autoClose: 3000 }))
        .catch(err => {
          console.error("Clipboard API failed:", err);
          fallbackCopyTextToClipboard(text);
        });
    } else {
      fallbackCopyTextToClipboard(text);
    }
  };

  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.top = "0";
    textArea.style.left = "0";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      toast.success(successful ? "Table copied (fallback)!" : "Failed to copy table.", { autoClose: 3000 });
    } catch (err) {
      console.error("Fallback copy failed", err);
      toast.error("Fallback copy failed.", { autoClose: 4000 });
    }

    document.body.removeChild(textArea);
  };

  return (
    <div style={{ maxWidth: 900, margin: "auto", padding: 20 }}>
      <div style={{ textAlign: "center", marginBottom: 20 }}>
        <img src="https://cohere.ph/img/cohere-logo.jpg" alt="Cohere Logo" style={{ width: 250 }} />
      </div>

      <div style={{ textAlign: "center", marginBottom: 20, fontWeight: "bold" }}>
        Last Uploaded Date: {lastUploadedDate || "Loading..."}
      </div>

      <form
  
  style={{
    display: "flex",
    gap: 16,
    marginBottom: 30,
    alignItems: "flex-end",
    flexWrap: "wrap",
  }}
>
  {/* SEARCH */}
  <div>
    <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
      Search (Name or Employee ID)
    </label>
    <input
      type="text"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      placeholder="First name, Last name, Full name, or Employee ID"
      style={{
        padding: 8,
        borderRadius: 4,
        border: "1px solid #ccc",
        width: 250,
      }}
    />
  </div>

  {/* START DATE */}
  <div>
    <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
      Start Date
    </label>
    <input
      type="date"
      value={startDate}
      onChange={(e) => setStartDate(e.target.value)}
      style={{
        padding: 8,
        borderRadius: 4,
        border: "1px solid #ccc",
        width: 150,
      }}
    />
  </div>

  {/* END DATE */}
  <div>
    <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
      End Date
    </label>
    <input
      type="date"
      value={endDate}
      onChange={(e) => setEndDate(e.target.value)}
      style={{
        padding: 8,
        borderRadius: 4,
        border: "1px solid #ccc",
        width: 150,
      }}
    />
  </div>

  {/* TRAINEE FILTER (NEW) */}
  <div>
    <label style={{ display: "block", marginBottom: 4, fontWeight: 600 }}>
      Employee Type
    </label>
    <select
      value={traineeFilter}
      onChange={(e) => setTraineeFilter(e.target.value)}
      style={{
        padding: 8,
        borderRadius: 4,
        border: "1px solid #ccc",
        width: 160,
      }}
    >
      <option value="">All</option>
      <option value="trainee">Trainee</option>
      <option value="non-trainee">Non-Trainee</option>
    </select>
  </div>

  
</form>

      <button
        onClick={handleCopyTable}
        style={{
          backgroundColor: "#4B5563",
          color: "white",
          padding: "10px 20px",
          borderRadius: 6,
          border: "none",
          cursor: "pointer",
          fontWeight: "bold",
          marginBottom: 20,
        }}
      >
        Copy Table
      </button>

      <table ref={tableRef} style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thStyle}>companyid</th>
            <th style={thStyle}>First Name</th>
            <th style={thStyle}>Last Name</th>
            <th style={thStyle}>Day</th>
            <th style={thStyle}>Time</th>
            <th style={thStyle}>Type</th>
          </tr>
        </thead>
        <tbody>
          {records.length === 0 ? (
            <tr>
              <td colSpan="6" style={{ textAlign: "center", padding: 12 }}>No records found.</td>
            </tr>
          ) : (
            records.map((rec, i) => (
              <tr key={i}>
                <td style={tdStyle}>{rec.companyid}</td>
                <td style={tdStyle}>{rec.FirstName}</td>
                <td style={tdStyle}>{rec.LastName}</td>
                <td style={tdStyle}>{rec.Day}</td>
                <td style={tdStyle}>{rec.Time}</td>
                <td style={tdStyle}>{rec.Type}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
}

const thStyle = { border: "1px solid #ccc", padding: 8 };
const tdStyle = { border: "1px solid #ccc", padding: 8 };
